import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Button,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import { 
  SunIcon, 
  MoonIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  AcademicCapIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppHandlers } from '../hooks/useAppHandlers';

const AppNavbar = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { state, actions } = useAppContext();
  const { theme, loading, supervisorLoading, lawyerLoading } = state;
  const { toggleTheme, setModalState } = actions;
  
  const { 
    generateAnalysis, 
    generateSupervisorReview, 
    generateLawyerReview 
  } = useAppHandlers();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleSupervisorReviewClick = () => {
    generateSupervisorReview();
    setModalState('supervisor', true);
  };

  const handleLawyerReviewClick = () => {
    generateLawyerReview();
    setModalState('lawyer', true);
  };

  const handleQuickDraft = () => {
    setModalState('quickDraft', true);
  };

  const onContactModalOpen = () => {
    setModalState('contact', true);
  };

  return (
    <Navbar 
      maxWidth="full" 
      className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700"
    >
      <NavbarBrand>
        <div className="flex items-center gap-2">
          <img src="/AX Logo.png" alt="AX Logo" className="h-8 w-auto" />
          <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            ApexionFlow
          </span>
        </div>
      </NavbarBrand>

      <NavbarContent className="hidden md:flex gap-4" justify="center">
        <NavbarItem>
          <div className="tooltip-container" data-tooltip="Get AI-powered suggestions to improve the your writing.">
            <Button
              onPress={generateAnalysis}
              color="primary"
              variant="flat"
              startContent={<AcademicCapIcon className="h-4 w-4" />}
              isLoading={loading}
            >
              AI Suggestions
            </Button>
          </div>
        </NavbarItem>
        <NavbarItem>
          <div className="tooltip-container" data-tooltip="Get feedback from an AI supervisor to improve your report.">
            <Button
              onPress={handleSupervisorReviewClick}
              color="secondary"
              variant="flat"
              startContent={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
              isLoading={supervisorLoading}
            >
              Supervisor Review
            </Button>
          </div>
        </NavbarItem>
        <NavbarItem>
          <div className="tooltip-container" data-tooltip="Get legal analysis and recommendations from an AI lawyer.">
            <Button
              onPress={handleLawyerReviewClick}
              color="warning"
              variant="flat"
              startContent={<ScaleIcon className="h-4 w-4" />}
              isLoading={lawyerLoading}
            >
              Legal Review
            </Button>
          </div>
        </NavbarItem>
        <NavbarItem>
          <div className="tooltip-container" data-tooltip="Generate a quick draft of your report using AI.">
            <Button
              onPress={handleQuickDraft}
              color="success"
              variant="flat"
              startContent={<DocumentPlusIcon className="h-4 w-4" />}
            >
              Quick Draft
            </Button>
          </div>
        </NavbarItem>
        <NavbarItem>
          <div className="tooltip-container" data-tooltip="Go to the detective tools dashboard.">
            <Button
              onPress={() => navigate('/follow-up')}
              color="default"
              variant="flat"
              startContent={<MagnifyingGlassIcon className="h-4 w-4" />}
            >
              Follow Up
            </Button>
          </div>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent justify="end">
        <NavbarItem>
          <Button
            isIconOnly
            variant="light"
            onPress={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? <MoonIcon className="h-5 w-5" /> : <SunIcon className="h-5 w-5" />}
          </Button>
        </NavbarItem>
        <NavbarItem>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Avatar
                isBordered
                as="button"
                className="transition-transform"
                color="primary"
                name={currentUser?.displayName || currentUser?.email}
                size="sm"
                src={currentUser?.photoURL}
                fallback={<UserIcon className="h-4 w-4" />}
              />
            </DropdownTrigger>
            <DropdownMenu aria-label="Profile Actions" variant="flat">
              <DropdownItem key="profile" className="h-14 gap-2">
                <p className="font-semibold">Signed in as</p>
                <p className="font-semibold">{currentUser?.email}</p>
              </DropdownItem>
              <DropdownItem key="settings" onPress={() => navigate('/profile')}>
                My Settings
              </DropdownItem>
              <DropdownItem key="contact" onPress={onContactModalOpen}>
                Contact Us
              </DropdownItem>
              <DropdownItem key="logout" color="danger" onPress={handleLogout} startContent={<ArrowRightOnRectangleIcon className="h-4 w-4" />}>
                Log Out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarItem>
      </NavbarContent>
    </Navbar>
  );
};

export default AppNavbar; 